import { Link, createFileRoute } from "@tanstack/react-router";
import { ExpandableIntro } from "@/components/expandable-intro";

import { ArticleBody, ArticleToc } from "@/components/article-body";
import { AuthorByline } from "@/components/author-byline";
import { CasinoList } from "@/components/casino-list";
import { EditorialTeam } from "@/components/editorial-team";
import { EeatTrustBlocks, HowWeTested } from "@/components/eeat-blocks";
import { PayoutSpeedInfographic } from "@/components/infographics";
import { NewCasinoTimelineInfographic, NewVsEstablishedInfographic } from "@/components/trust-new-infographics";
import { KeyTakeaways } from "@/components/key-takeaways";
import { QualityGuarantee } from "@/components/quality-guarantee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { guide } from "@/content/guides/nieuwe-online-casinos";
import heroImage from "@/assets/articles/testing-branded.webp";
import { getAuthor } from "@/lib/authors";
import { LinkBudget, linkifyText } from "@/lib/internal-links";
import { ogImageMeta } from "@/lib/og";
import { getPublicCasinos } from "@/lib/queries";
import { SITE } from "@/lib/site";

const PATH = "/nieuwe-online-casinos";
const URL = `${SITE.url}${PATH}/`;

export const Route = createFileRoute("/nieuwe-online-casinos")({
  head: () => {
    const author = getAuthor(guide.authorSlug);
    const reviewer = getAuthor(guide.reviewerSlug);
    return {
      meta: [
        { title: "Nieuwe Online Casino's Nederland 2026: Top 10 Nieuwe Casino's" },
        { name: "description", content: guide.description },
        { property: "og:title", content: "Nieuwe Online Casino's Nederland 2026: Top 10 Nieuwe Casino's" },
        { property: "og:description", content: guide.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...ogImageMeta("nieuwe-online-casinos"),
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            inLanguage: "nl-NL",
            mainEntityOfPage: URL,
            dateModified: "2026-11-03",
            author: author
              ? { "@type": "Person", name: author.name, jobTitle: author.role }
              : { "@type": "Organization", name: SITE.name },
            reviewedBy: reviewer
              ? { "@type": "Person", name: reviewer.name, jobTitle: reviewer.role }
              : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: guide.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Nieuwe online casino's", item: URL },
            ],
          }),
        },
      ],
    };
  },
  component: Page,
});

function Page() {
  const casinos = getPublicCasinos();
  const budget = new LinkBudget(PATH);

  const takeaways = ["Nieuwe casino's zijn pas interessant als de kassa op orde is: vergunning, iDEAL en een vermelde opnametijd.", "Test elk nieuw casino met een kleine iDEAL-storting en een opname van € 20 tot € 50.", "Verifieer direct na registratie, dan staat je eerste uitbetaling niet stil.", "Wakuna is het jongste merk in onze lijst: live sinds oktober 2026.", "Een grote welkomstbonus blokkeert je opname tot je hebt rondgespeeld.", "Nieuwe buitenlandse sites zonder Nederlandse vergunning raden we af."];

  return (
    <main className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-12">
      <nav aria-label="Broodkruimels" className="mb-2 text-xs text-muted-foreground sm:mb-6 sm:text-sm">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Nieuwe online casino's</span>
      </nav>

      <h1 className="max-w-4xl text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
        Nieuwe online casino's in Nederland: kassa en uitbetaling getest
      </h1>

      <div className="mt-2 max-w-3xl sm:mt-6">
        <AuthorByline
          authorSlug={guide.authorSlug}
          reviewerSlug={guide.reviewerSlug}
          updatedAt={guide.updatedAt}
          readingMinutes={guide.readingMinutes}
        />
      </div>
      <ExpandableIntro>{guide.description}</ExpandableIntro>

      <div className="mt-8 hidden max-w-3xl sm:block">
        <KeyTakeaways items={takeaways} />
      </div>

      <div className="mt-4 grid gap-10 sm:mt-10 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0">
          <section aria-labelledby="casinos">
            <h2 id="casinos" className="text-xl font-semibold text-foreground">
              Top 10 nieuwe online casino's in Nederland
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Dezelfde vergelijkingslijst als elders op de site, beoordeeld op vergunning, kassa en uitbetaling.
            </p>
            <div className="mt-4">
              <CasinoList casinos={casinos} />
            </div>
            <figure className="mt-6 overflow-hidden rounded-xl border border-border">
              <img
                src={heroImage}
                alt="Nieuwe online casino's in Nederland: kassa met iDEAL, kaart en wallet getest op stortingen en uitbetalingen"
                width={1280}
                height={720}
                loading="lazy"
                decoding="async"
                className="aspect-[21/9] w-full object-cover"
              />
              <figcaption className="border-t border-border bg-secondary/50 px-4 py-2 text-xs text-muted-foreground">
                Beeld: redactie {SITE.name}
              </figcaption>
            </figure>
          </section>

          <div className="mt-10">
            <HowWeTested
              subject="kassa en eerste uitbetaling bij nieuwe casino's"
              providers={10}
              period="november 2026"
            />
          </div>

          <div className="mt-10">
            <NewCasinoTimelineInfographic />
          </div>

          <section className="mt-12" aria-label="Alles over betaallimieten bij online casino's">
            <ArticleBody blocks={guide.blocks} currentPath={PATH} budget={budget} />
          </section>

          <div className="mt-14 grid gap-6">
            <NewVsEstablishedInfographic />
            <PayoutSpeedInfographic />
          </div>

          <div className="mt-14">
            <EditorialTeam
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
              methodName="nieuwe online casino's"
            />
          </div>

          <section className="mt-12" aria-labelledby="faq">
            <h2 id="faq" className="text-xl font-semibold text-foreground">
              Veelgestelde vragen over nieuwe online casino's
            </h2>
            <Accordion type="single" collapsible className="mt-4">
              {guide.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {linkifyText(item.a, budget, `faq-${i}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="mt-14">
            <EeatTrustBlocks authorSlug={guide.authorSlug} reviewerSlug={guide.reviewerSlug} updatedAt={guide.updatedAt} />
          </div>
        </div>

        <aside className="min-w-0 space-y-6 lg:sticky lg:top-32 lg:self-start">
          <ArticleToc blocks={guide.blocks} />
          <div className="rounded-lg border border-border bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            {SITE.disclaimer}
          </div>
        </aside>
      </div>

      <div className="mt-14">
        <QualityGuarantee />
      </div>
    </main>
  );
}
